import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { Context } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ApiExames from "../services/exameService";
import ApiAlunos from "../services/alunoService";
import { validarElegibilidadeExame } from "../utils/validarElegibilidadeExame";
import FormaPagamento from '../components/FormaPagamento';

const ExamesInscricao = ({ exame }) => {
    const { handleLogin, handleLogout } = useContext(Context);
    const navigate = useNavigate();
    const boxPedidoRef = useRef(null);
    const user = useMemo(() => {
        return JSON.parse(localStorage.getItem("user"));
    }, []);

    const accessToken = localStorage.getItem("token");

    const [aluno, setAluno] = useState(null);
    const [graduacao, setGraduacao] = useState(null);
    const [cpf, setCpf] = useState("");
    const [pedido, setPedido] = useState(null);
    const [mensagemBloqueio, setMensagemBloqueio] = useState(null);

    const [isLoadingAluno, setIsLoadingAluno] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const nome = user?.name || "";
    const email = user?.email || "";

    /* ============================================================
       🔹 Buscar dados do aluno
    ============================================================ */

    const getAluno = async (userId) => {
        setIsLoadingAluno(true);

        try {
            const response = await ApiAlunos.getAlunoPorUserId(
                userId,
                accessToken
            );

            if (response.statusRetorno === 401) {
                handleLogout();
                navigate("/");
                return;
            }

            const alunoData = response.data?.aluno?.[0] ?? null;
            const graduacaoData = response.data?.graduacao?.[0] ?? null;

            setAluno(alunoData);
            setGraduacao(graduacaoData);
            setCpf(alunoData?.cpf || "");

        } catch (error) {
            console.error("Erro ao buscar dados do aluno:", error);
        } finally {
            setIsLoadingAluno(false);
        }
    };

    /* ============================================================
       🔹 Carregar aluno se estiver logado
    ============================================================ */

    useEffect(() => {
        if (user?.id) {
            getAluno(user.id);
        }
    }, [user]);

    /* ============================================================
       🔹 Validação de elegibilidade
    ============================================================ */

    useEffect(() => {
        if (!user?.id) return;
        if (isLoadingAluno) return;
        if (!exame) return;
        if (!aluno) return;

        const resultado = validarElegibilidadeExame({
            graduacao,
            exame,
            nome,
            aluno
        });

        if (!resultado.permitido) {
            setMensagemBloqueio(resultado.mensagem);
        } else {
            setMensagemBloqueio(null);
        }

    }, [user, aluno, graduacao, exame, isLoadingAluno, nome]);

    /* ============================================================
       🔹 Login Popup
    ============================================================ */

    const showLoginPopup = () => {
        Swal.fire({
            title: "Acesso para Filiados",
            html: `
                <input type="email" id="swal-input-email" class="swal2-input" placeholder="Digite seu email">
                <input type="password" id="swal-input-password" class="swal2-input" placeholder="Digite sua senha">
            `,
            showCancelButton: true,
            confirmButtonText: "Entrar",
            cancelButtonText: "Cancelar",
            preConfirm: () => {
                const email = document.getElementById("swal-input-email").value;
                const password = document.getElementById("swal-input-password").value;

                if (!email || !password) {
                    Swal.showValidationMessage(
                        "Por favor, preencha todos os campos!"
                    );
                    return false;
                }

                return { email, password };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const { email, password } = result.value;

                handleLogin(email, password, () => {
                    Swal.fire({
                        icon: "success",
                        title: "Login realizado com sucesso!",
                        showConfirmButton: false,
                        timer: 1500
                    }).then(() => navigate(0));
                });
            }
        });
    };

    /* ============================================================
       🔹 Pagamento
    ============================================================ */
    const handlePagamento = async () => {
        if (isProcessing) return;

        if (mensagemBloqueio) {
            Swal.fire({
                icon: "warning",
                text: mensagemBloqueio
            });
            return;
        }

        if (!nome || !email || !cpf) {
            Swal.fire({
                icon: "warning",
                text: "Todos os campos são obrigatórios!"
            });
            return;
        }

        if (!graduacao?.faixa_id && !graduacao?.id) {
            Swal.fire({
                icon: "warning",
                text: "Graduação não encontrada."
            });
            return;
        }

        setIsProcessing(true);

        try {
            const data = {
                nome,
                email,
                cpf,
                exameId: exame?.id,
                usuarioId: user?.id ?? 0,
                graduacaoId: graduacao?.faixa_id ?? graduacao?.id
            };

            const response = await ApiExames.gerarPedidoExame(data);

            if (response.statusRetorno === 401) {
                handleLogout();
                navigate("/");
                return;
            }

            if (response.statusRetorno !== 200) {
                Swal.fire({
                    icon: "error",
                    title: "Erro ao gerar inscrição",
                    text: response.message
                });
                return;
            }

            setPedido(response.pedido);
            setTimeout(() => {
                boxPedidoRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100); 

        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Erro ao processar pagamento",
                text: error.message || "Tente novamente mais tarde."
            });
        } finally {
            setIsProcessing(false);
        }
    };

    /* ============================================================
       🔹 Render
    ============================================================ */

    return (
        <div className="container">
            <div className="row position-relative">
                <div className="col-lg-12">

                    {!user?.id ? (
                        <input
                            type="button"
                            value="Realizar Inscrição"
                            className="btn-main color-2"
                            onClick={showLoginPopup}
                        />
                    ) : isLoadingAluno ? (
                        <p>Carregando informações...</p>
                    ) : (
                        <div className="col-md-12">

                            {mensagemBloqueio ? (
                                <div className="alert alert-warning">
                                    {mensagemBloqueio}
                                </div>
                            ) : (
                                pedido === null && (
                                    <button 
                                        className="btn-main color-2"
                                        disabled={isProcessing}
                                        onClick={handlePagamento}
                                    >
                                        {isProcessing
                                            ? "Processando..."
                                            : "Confirmar Inscrição"}
                                    </button>
                                )
                            )}

                            {pedido !== null &&
                                <>
                                    <h4 className="opt-1"><span>Boleto gerado com sucesso!</span></h4>
                                    <p>* Seu boleto e QRCODE para pagamento estão disponíveis abaixo! </p>
                                </>
                                
                            }

                            {(pedido !== null && pedido?.status !== 1) &&
                                <div className="row mt-5" id="boxPedido">
                                    <br /><br />
                                    <div className="col-xl-12 col-md-6 mb-sm-30 fadeInRight mb-5" >
                                        <div className={`de_pricing-table type-2`}>
                                            <FormaPagamento
                                                pedido={pedido}
                                            />
                                        </div>
                                    </div>
                                </div>
                            } 

                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ExamesInscricao;